<template>
  <PublicLayout page-title="Términos y condiciones">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
      <!-- Page header -->
      <header class="text-center mb-12">
        <h1 class="text-3xl md:text-4xl font-bold text-legal-900 mb-4 tracking-tight">
          {{ pageTitle }}
        </h1>
        <p class="text-lg text-legal-600 max-w-2xl mx-auto leading-relaxed">
          {{ pageDescription }}
        </p>
        <div class="flex items-center justify-center space-x-4 mt-6 text-sm text-legal-500">
          <span class="flex items-center">
            <CalendarDaysIcon class="w-4 h-4 mr-1" aria-hidden="true" />
            Actualizada: {{ lastUpdated }}
          </span>
          <span class="flex items-center">
            <ClipboardDocumentListIcon class="w-4 h-4 mr-1" aria-hidden="true" />
            Versión: {{ termsVersion }}
          </span>
        </div>
      </header>

      <!-- Content sections -->
      <article class="space-y-12">
        
        <!-- Section 1: Información General -->
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">1. Información General y Contacto</h2>
          <div class="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-slate-200">
            <div class="text-legal-700 space-y-2">
              <p><span class="font-medium">Responsable:</span> {{ RESPONSABLE_TRATAMIENTO.nombre }}</p>
              <p><span class="font-medium">NIF:</span> {{ RESPONSABLE_TRATAMIENTO.nif }}</p>
              <p><span class="font-medium">Nombre comercial:</span> {{ RESPONSABLE_TRATAMIENTO.nombreComercial }}</p>
              <p><span class="font-medium">Domicilio:</span> {{ RESPONSABLE_TRATAMIENTO.domicilio }}</p>
              <p><span class="font-medium">Email:</span> {{ RESPONSABLE_TRATAMIENTO.email }}</p>
            </div>
          </div>
          
          <p class="text-legal-600 leading-relaxed">
            Los presentes términos y condiciones regulan el uso de nuestros servicios especializados en protección 
            de datos, ejercicio del derecho al olvido y gestión de herencias digitales. Al contratar nuestros servicios, 
            usted acepta estos términos en su totalidad.
          </p>
        </section>

        <!-- Section 2: Servicios -->
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">2. Descripción de Servicios</h2>
          
          <div class="grid gap-6 md:grid-cols-3">
            <div class="bg-white border border-slate-200 rounded-2xl p-6 hover:transform hover:scale-105 transition-all duration-200 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <ShieldCheckIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Derecho al Olvido
              </h3>
              <ul class="space-y-2 text-legal-600 text-sm">
                <li>• Solicitudes de eliminación de datos</li>
                <li>• Seguimiento de plataformas</li>
                <li>• Informes de cumplimiento</li>
                <li>• Asesoría legal especializada</li>
              </ul>
            </div>
            
            <div class="bg-white border border-slate-200 rounded-2xl p-6 hover:transform hover:scale-105 transition-all duration-200 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <div class="w-5 h-5 mr-2 bg-teal-600 rounded flex items-center justify-center" aria-hidden="true">
                  <div class="w-3 h-3 bg-white rounded-sm opacity-80"></div>
                </div>
                Gestión Post-Mortem
              </h3>
              <ul class="space-y-2 text-legal-600 text-sm">
                <li>• Herencias digitales</li>
                <li>• Memorización y eliminación</li>
                <li>• Gestión de cuentas del fallecido</li>
                <li>• Documentación legal</li>
              </ul>
            </div>
            
            <div class="bg-white border border-slate-200 rounded-2xl p-6 hover:transform hover:scale-105 transition-all duration-200 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <DocumentTextIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Testamento Digital
              </h3>
              <ul class="space-y-2 text-legal-600 text-sm">
                <li>• Instrucciones sobre tu legado digital</li>
                <li>• Inventario de cuentas digitales</li>
                <li>• Documento legalmente válido</li>
              </ul>
            </div>
          </div>
        </section>

        <!-- Section 3: Planes y Tarifas -->
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">3. Planes de Servicio y Tarifas</h2>
          
          <div class="overflow-x-auto">
            <table class="w-full border border-slate-200 rounded-2xl bg-white shadow-lg">
              <thead class="bg-gradient-to-r from-teal-50 to-teal-100">
                <tr>
                  <th class="px-6 py-3 text-left text-sm font-medium text-legal-900">Servicio</th>
                  <th class="px-6 py-3 text-left text-sm font-medium text-legal-900">Precio</th>
                  <th class="px-6 py-3 text-left text-sm font-medium text-legal-900">URLs</th>
                  <th class="px-6 py-3 text-left text-sm font-medium text-legal-900">Características</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-legal-200">
                <tr class="bg-teal-50/30">
                  <td class="px-6 py-4 text-sm font-medium text-teal-900">Derecho al Olvido</td>
                  <td class="px-6 py-4 text-sm text-teal-700 font-semibold">499€</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Hasta 5</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Solicitudes GDPR profesionales, soporte email</td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm font-medium text-legal-900">Derecho al Olvido Post-Mortem</td>
                  <td class="px-6 py-4 text-sm text-legal-700">699€</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Hasta 5</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Gestión de legado digital, soporte dedicado</td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm font-medium text-legal-900">Testamento Digital</td>
                  <td class="px-6 py-4 text-sm text-legal-700">599€</td>
                  <td class="px-6 py-4 text-sm text-legal-600">N/A</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Instrucciones sobre tu legado digital, documento legal</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="mt-6 bg-teal-50 rounded-2xl p-6 border border-teal-100">
            <h3 class="font-medium text-legal-900 mb-3 flex items-center">
              <CreditCardIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
              Condiciones de pago
            </h3>
            <ul class="space-y-2 text-legal-600 text-sm">
              <li>• <span class="font-medium">Pago único:</span> No hay suscripciones recurrentes</li>
              <li>• <span class="font-medium">Métodos aceptados:</span> Tarjeta de crédito/débito, transferencia bancaria</li>
              <li>• <span class="font-medium">Facturación:</span> Emitimos factura legal con IVA incluido</li>
              <li>• <span class="font-medium">Reembolsos:</span> 14 días de garantía según condiciones</li>
            </ul>
          </div>
        </section>

        <!-- Section 4: Obligaciones -->
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">4. Obligaciones del Cliente</h2>
          
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
            <h3 class="font-medium text-legal-900 mb-4">El cliente se compromete a:</h3>
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <h4 class="font-medium text-legal-900 mb-2 flex items-center">
                  <ClipboardDocumentCheckIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                  Información veraz
                </h4>
                <ul class="space-y-1 text-legal-600 text-sm">
                  <li>• Proporcionar datos exactos y actualizados</li>
                  <li>• Comunicar cambios relevantes</li>
                  <li>• Colaborar en el proceso legal</li>
                </ul>
              </div>
              <div>
                <h4 class="font-medium text-legal-900 mb-2 flex items-center">
                  <LockClosedIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                  Confidencialidad
                </h4>
                <ul class="space-y-1 text-legal-600 text-sm">
                  <li>• Mantener confidencialidad mutua</li>
                  <li>• No divulgar estrategias legales</li>
                  <li>• Respetar términos de privacidad</li>
                </ul>
              </div>
              <div>
                <h4 class="font-medium text-legal-900 mb-2 flex items-center">
                  <CreditCardIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                  Pagos
                </h4>
                <ul class="space-y-1 text-legal-600 text-sm">
                  <li>• Abonar servicios según tarifa</li>
                  <li>• Cumplir plazos de pago</li>
                  <li>• Notificar incidencias de facturación</li>
                </ul>
              </div>
              <div>
                <h4 class="font-medium text-legal-900 mb-2 flex items-center">
                  <ScaleIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                  Uso legítimo
                </h4>
                <ul class="space-y-1 text-legal-600 text-sm">
                  <li>• Usar servicios conforme a derecho</li>
                  <li>• No realizar solicitudes fraudulentas</li>
                  <li>• Respetar derechos de terceros</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <!-- Section 5: Proceso de Contratación -->
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">5. Proceso de contratación</h2>
          
          <div class="space-y-6">
            <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <ClipboardDocumentListIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Fases del servicio
              </h3>
              <div class="space-y-4">
                <div class="flex items-start space-x-3">
                  <span class="bg-teal-100 text-teal-700 px-2 py-1 rounded text-sm font-medium">1</span>
                  <div>
                    <h4 class="font-medium text-legal-900">Registro y evaluación inicial</h4>
                    <p class="text-legal-600 text-sm mt-1">Análisis del caso, recopilación de documentación necesaria</p>
                  </div>
                </div>
                <div class="flex items-start space-x-3">
                  <span class="bg-teal-100 text-teal-700 px-2 py-1 rounded text-sm font-medium">2</span>
                  <div>
                    <h4 class="font-medium text-legal-900">Pago y confirmación</h4>
                    <p class="text-legal-600 text-sm mt-1">Abono del servicio según tarifa seleccionada</p>
                  </div>
                </div>
                <div class="flex items-start space-x-3">
                  <span class="bg-teal-100 text-teal-700 px-2 py-1 rounded text-sm font-medium">3</span>
                  <div>
                    <h4 class="font-medium text-legal-900">Ejecución del servicio</h4>
                    <p class="text-legal-600 text-sm mt-1">Preparación y envío de documentos legales</p>
                  </div>
                </div>
                <div class="flex items-start space-x-3">
                  <span class="bg-teal-100 text-teal-700 px-2 py-1 rounded text-sm font-medium">4</span>
                  <div>
                    <h4 class="font-medium text-legal-900">Seguimiento y entrega</h4>
                    <p class="text-legal-600 text-sm mt-1">Comunicación de resultados y documentación final</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="bg-teal-50 rounded-2xl p-6 border border-teal-100">
              <h3 class="font-medium text-legal-900 mb-3 flex items-center">
                <ClockIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Plazos de ejecución orientativos
              </h3>
              <ul class="space-y-2 text-legal-700 text-sm">
                <li>• <span class="font-medium">Análisis inicial:</span> en el menor plazo posible</li>
                <li>• <span class="font-medium">Preparación de documentos:</span> 3-5 días laborables</li>
                <li>• <span class="font-medium">Gestiones con plataformas:</span> 15-30 días (varía según plataforma)</li>
                <li>• <span class="font-medium">Informe final:</span> Dentro de 48 horas tras recibir respuestas</li>
              </ul>
            </div>
          </div>
        </section>

        <!-- Section 6: Garantías y Limitaciones -->
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">6. Garantías y limitaciones de responsabilidad</h2>
          
          <div class="space-y-6">
            <div class="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 class="font-medium text-green-900 mb-3 flex items-center">
                <CheckCircleIcon class="w-5 h-5 mr-2 text-green-600" aria-hidden="true" />
                Nuestras garantías
              </h3>
              <ul class="space-y-2 text-green-800 text-sm">
                <li>• <span class="font-medium">Cumplimiento normativo:</span> Todas nuestras gestiones cumplen GDPR y LOPDGDD</li>
                <li>• <span class="font-medium">Profesionalidad:</span> Servicio prestado por abogados especializados</li>
                <li>• <span class="font-medium">Confidencialidad:</span> Protección absoluta de sus datos personales</li>
                <li>• <span class="font-medium">Transparencia:</span> Seguimiento detallado del progreso en tiempo real</li>
                <li>• <span class="font-medium">Soporte especializado:</span> Atención personalizada durante todo el proceso</li>
                <li>• <span class="font-medium">Garantía de devolución:</span> 14 días para solicitar reembolso (condiciones específicas)</li>
              </ul>
            </div>
            
            <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 class="font-medium text-yellow-900 mb-3 flex items-center">
                <ExclamationTriangleIcon class="w-5 h-5 mr-2 text-yellow-600" aria-hidden="true" />
                Limitaciones de responsabilidad
              </h3>
              <ul class="space-y-2 text-yellow-800 text-sm">
                <li>• <span class="font-medium">Dependencia de terceros:</span> Los resultados dependen de la colaboración de plataformas externas</li>
                <li>• <span class="font-medium">Plazos variables:</span> Los tiempos de respuesta varían según cada plataforma</li>
                <li>• <span class="font-medium">Casos complejos:</span> Algunos supuestos requieren procedimientos judiciales adicionales</li>
                <li>• <span class="font-medium">Límites legales:</span> Respetamos la libertad de expresión e interés público legítimo</li>
                <li>• <span class="font-medium">Exclusión de garantía de resultado:</span> Garantizamos medios, no resultados específicos</li>
                <li>• <span class="font-medium">Límite de responsabilidad:</span> Máximo el importe abonado por el servicio</li>
              </ul>
            </div>
            
            <div class="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 class="font-medium text-red-900 mb-3 flex items-center">
                <XCircleIcon class="w-5 h-5 mr-2 text-red-600" aria-hidden="true" />
                Casos de exoneración
              </h3>
              <ul class="space-y-2 text-red-800 text-sm">
                <li>• Información falsa o incompleta proporcionada por el cliente</li>
                <li>• Fuerza mayor o casos fortuitos</li>
                <li>• Cambios en la legislación aplicable</li>
                <li>• Decisiones judiciales en contra de nuestras gestiones</li>
                <li>• Negativa fundada en derecho de las plataformas</li>
              </ul>
            </div>
          </div>
        </section>

        <!-- Section 7: Propiedad Intelectual -->
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">7. Propiedad Intelectual</h2>
          
          <div class="space-y-6">
            <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4">Derechos de Autor y Marcas</h3>
              <p class="text-legal-600 mb-4">
                Todos los contenidos de este sitio web, incluyendo textos, gráficos, logotipos, iconos, 
                imágenes, clips de audio, descargas digitales y compilaciones de datos, son propiedad 
                de OLVIDADME o sus proveedores de contenido.
              </p>
              <ul class="space-y-2 text-legal-600 text-sm">
                <li>• Prohibida la reproducción parcial o total sin autorización</li>
                <li>• Uso personal y no comercial permitido</li>
                <li>• Licencias de software de terceros respetadas</li>
                <li>• Contenido generado por usuarios sujeto a nuestras políticas</li>
              </ul>
            </div>
            
            <div class="bg-teal-50 rounded-2xl p-6 border border-teal-100">
              <h3 class="font-medium text-legal-900 mb-4">Licencia de Uso</h3>
              <p class="text-legal-600 text-sm">
                Se concede una licencia limitada, no exclusiva e intransferible para usar 
                nuestros servicios conforme a estos términos y condiciones.
              </p>
            </div>
          </div>
        </section>

        <!-- Section 8: Duración y Terminación -->
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">8. Duración y Terminación del Servicio</h2>
          
          <div class="grid gap-6 md:grid-cols-2">
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <ClockIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Duración del Contrato
              </h3>
              <ul class="space-y-2 text-legal-600 text-sm">
                <li>• <span class="font-medium">Por servicio:</span> Hasta completar las gestiones contratadas</li>
                <li>• <span class="font-medium">Plazo máximo:</span> 12 meses desde la contratación</li>
                <li>• <span class="font-medium">Prórroga:</span> Solo por acuerdo expreso de ambas partes</li>
                <li>• <span class="font-medium">Servicios adicionales:</span> Contratación independiente</li>
              </ul>
            </div>
            
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <XCircleIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Causas de Terminación
              </h3>
              <ul class="space-y-2 text-legal-600 text-sm">
                <li>• Cumplimiento del objeto del contrato</li>
                <li>• Incumplimiento grave de las obligaciones</li>
                <li>• Imposibilidad sobrevenida de prestación</li>
                <li>• Mutuo acuerdo de las partes</li>
                <li>• Ejercicio del derecho de desistimiento (14 días)</li>
              </ul>
            </div>
          </div>
          
          <div class="mt-6 bg-teal-50 rounded-2xl p-6 border border-teal-100">
            <h3 class="font-medium text-legal-900 mb-3">Efectos de la Terminación</h3>
            <p class="text-legal-600 text-sm">
              En caso de terminación anticipada del contrato, el cliente tendrá derecho a recibir 
              todos los documentos y gestiones realizadas hasta la fecha de terminación. 
              Los datos personales serán tratados conforme a nuestra política de privacidad.
            </p>
          </div>
        </section>

        <!-- Section 9: Modificaciones -->
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">9. Modificaciones de los Términos</h2>
          
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
            <p class="text-legal-600 mb-4">
              Nos reservamos el derecho a modificar estos términos y condiciones en cualquier momento, 
              previa notificación a nuestros usuarios a través de:
            </p>
            <ul class="space-y-2 text-legal-600 text-sm mb-4">
              <li>• Email a la dirección registrada</li>
              <li>• Notificación en nuestro sitio web</li>
              <li>• Aviso en su panel de usuario</li>
            </ul>
            <p class="text-legal-600 text-sm">
              <span class="font-medium">Plazo de notificación:</span> Mínimo 15 días naturales antes de la entrada en vigor. 
              El uso continuado de nuestros servicios implica la aceptación de las modificaciones.
            </p>
          </div>
        </section>

        <!-- Section 10: Reclamaciones y Resolución de Conflictos -->
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">10. Reclamaciones y Resolución de Conflictos</h2>
          
          <div class="space-y-6">
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-3 flex items-center">
                <ChatBubbleLeftEllipsisIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Procedimiento de Reclamaciones
              </h3>
              <div class="space-y-3">
                <div>
                  <h4 class="font-medium text-legal-900 text-sm">1. Contacto Directo</h4>
                  <p class="text-legal-700 text-sm">Email: {{ RESPONSABLE_TRATAMIENTO.emailSoporte }} | Plazo respuesta: 48 horas</p>
                </div>
                <div>
                  <h4 class="font-medium text-legal-900 text-sm">2. Mediación</h4>
                  <p class="text-legal-700 text-sm">Si no se alcanza solución, mediación a través de organismo oficial</p>
                </div>
                <div>
                  <h4 class="font-medium text-legal-900 text-sm">3. Arbitraje</h4>
                  <p class="text-legal-700 text-sm">Corte Aragonesa de Arbitraje y Mediacion (Camara de Comercio de Zaragoza)</p>
                </div>
              </div>
            </div>
            
            <div class="bg-teal-50 rounded-2xl p-6 border border-teal-100">
              <h3 class="font-medium text-legal-900 mb-3">Plataforma ODR</h3>
              <p class="text-legal-600 text-sm">
                Para la resolución de litigios en línea: 
                <a href="https://ec.europa.eu/consumers/odr" 
                   class="text-teal-600 hover:text-teal-700 underline decoration-teal-600/30"
                   target="_blank" rel="noopener">
                  Plataforma de Resolución de Litigios en Línea de la UE
                </a>
              </p>
            </div>
          </div>
        </section>

        <!-- Section 11: Jurisdicción y Ley Aplicable -->
        <section>
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">11. Jurisdicción y Ley Aplicable</h2>
          
          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
            <div class="space-y-4">
              <div>
                <h3 class="font-medium text-legal-900 mb-2">Ley Aplicable</h3>
                <p class="text-legal-600 text-sm">
                  Estos términos y condiciones se rigen por la legislación española, específicamente:
                </p>
                <ul class="space-y-1 text-legal-600 text-sm mt-2 ml-4">
                  <li>• Reglamento General de Protección de Datos (RGPD)</li>
                  <li>• Ley Orgánica de Protección de Datos (LOPDGDD)</li>
                  <li>• Ley de Servicios de la Sociedad de la Información (LSSI)</li>
                  <li>• Código Civil y legislación mercantil aplicable</li>
                </ul>
              </div>
              <div>
                <h3 class="font-medium text-legal-900 mb-2">Jurisdiccion</h3>
                <p class="text-legal-600 text-sm">
                  <strong>Arbitraje B2B:</strong> Corte Aragonesa de Arbitraje y Mediacion
                  (Camara de Comercio de Zaragoza).
                </p>
                <p class="text-legal-600 text-sm mt-2">
                  <strong>B2C:</strong> Juzgados del domicilio del consumidor.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- Footer -->
        <footer class="text-center pt-8 border-t border-legal-200">
          <p class="text-legal-500 text-sm">
            Para cualquier consulta sobre estos términos y condiciones, contáctenos en
            <a :href="`mailto:${RESPONSABLE_TRATAMIENTO.email}`" class="text-teal-600 hover:text-teal-700 underline decoration-teal-600/30 underline-offset-4 hover:decoration-teal-700">
              {{ RESPONSABLE_TRATAMIENTO.email }}
            </a>
          </p>
        </footer>
      </article>
    </div>
  </PublicLayout>
</template>

<script setup>
import { ref } from 'vue'
import { useSeo } from '@/composables/useSeo'
import PublicLayout from '@/components/layout/PublicLayout.vue'
import { CreditCardIcon } from '@heroicons/vue/24/outline'
import { RESPONSABLE_TRATAMIENTO } from '@/utils/constants/legal.js'

// SEO Meta Tags
useSeo({
  title: 'Términos y Condiciones',
  description: 'Condiciones de uso de los servicios legales de Olvidadme. Derechos y obligaciones.',
  canonical: '/terminos-condiciones'
})

// Heroicons imports for professional legal appearance
import {
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  ScaleIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChatBubbleLeftEllipsisIcon
} from '@heroicons/vue/24/outline'
import {
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/vue/24/solid'

/**
 * TermsConditions Component for Olvidadme
 * 
 * Implementing Legal Pages Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Using pure Tailwind CSS v4 classes following project design system
 * 
 * Features:
 * - Pure Tailwind v4 styling (no custom CSS)
 * - Professional legal structure
 * - Service plans and pricing information
 * - Client obligations and guarantees
 * - Responsive design with mobile-first approach
 * - GDPR compliance information
 * 
 * Applies ADR-043: Vue Landing Components Architecture
 * - Professional Trust Pattern for legal authority
 * - Clean typography with Inter font system
 * - Legal specialization with service tiers
 */

// Page metadata
const pageTitle = ref('Términos y Condiciones')
const pageDescription = ref('Términos y condiciones que regulan la prestación de nuestros servicios legales especializados')
const lastUpdated = ref('08 de septiembre de 2025')
const termsVersion = ref('3.0')

// Analytics tracking
if (typeof window !== 'undefined' && window.gtag) {
  window.gtag('event', 'legal_page_view', {
    event_category: 'legal_compliance',
    event_label: 'terms_conditions',
    value: 1
  })
}
</script>